/**
 * Given a browser Selection object, extracts the full sentence containing
 * the selected text from the surrounding DOM.
 *
 * Returns a trimmed string (max 300 chars), or null if extraction fails.
 *
 * §3.13 — pre-fix, this used substring matching ("find first sentence
 * containing the selected word") on a `\s+`-collapsed copy of the whole
 * block's textContent. That produced two failure modes:
 *   (a) Block contained the word at multiple offsets → first match wins,
 *       even if the student actually selected a later occurrence.
 *   (b) Block-level sentence regex missed the boundary near the selection
 *       (smart-quote / dialogue / abbreviation) → fell back to the block's
 *       first 300 chars, which often didn't contain the selected word at
 *       all (Erica's `carafe` card surfaced this — context_sentence had
 *       no occurrence of "carafe").
 *
 * Post-fix:
 *   - Use the selection's actual char offset within the block to anchor.
 *   - Smart-quote- and abbreviation-aware sentence segmentation, mirroring
 *     the Ruby `TextSegmentation#segment_sentences_with_offsets` shape.
 *   - Fall back to substring match only when offset-lookup fails (selection
 *     anchored on a non-text node, etc.); fall back to the block's first
 *     300 chars only when both prior strategies miss.
 */
export function extractContextSentence(selection) {
  if (!selection || selection.isCollapsed || !selection.anchorNode) return null;

  const selectedText = selection.toString().trim();
  if (!selectedText) return null;

  // Walk up to find a block-level element containing the selection.
  let el = selection.anchorNode.nodeType === Node.TEXT_NODE
    ? selection.anchorNode.parentElement
    : selection.anchorNode;

  const BLOCK_TAGS = ['P', 'DIV', 'LI', 'BLOCKQUOTE', 'TD', 'SECTION', 'ARTICLE'];
  while (el && !BLOCK_TAGS.includes(el.tagName?.toUpperCase())) {
    el = el.parentElement;
  }
  if (!el) return selectedText;

  const fullText = el.textContent || '';

  // Char offset of the selection start within the block's textContent.
  // -1 if the selection isn't anchored on a text node we can locate.
  const range = selection.getRangeAt(0);
  const startOffset = charOffsetIn(el, range.startContainer, range.startOffset);

  const sentences = segmentSentencesWithOffsets(fullText);

  // Strategy 1: pick the sentence whose char span contains the selection
  // anchor. This is the only strategy that reliably disambiguates when
  // the same word appears multiple times in the block.
  let owner = null;
  if (startOffset >= 0) {
    owner = sentences.find((s) => startOffset >= s.start && startOffset < s.end);
  }

  // Strategy 2 (fallback): substring match — the legacy heuristic.
  if (!owner) {
    owner = sentences.find((s) =>
      s.text.toLowerCase().includes(selectedText.toLowerCase())
    );
  }

  const candidate = (owner?.text || fullText).trim();
  // Collapse whitespace runs for storage, then truncate. Paragraph-break
  // preservation lives server-side (FrequencyRankingService.build_context)
  // because the user-add flow captures from a single block — there are no
  // cross-paragraph breaks to preserve at this layer.
  return candidate.replace(/\s+/g, ' ').slice(0, 300) || null;
}

/**
 * Given a browser Selection, returns the single selected word (lemma candidate).
 * Returns null if the selection spans multiple words.
 */
export function extractSingleWord(selection) {
  if (!selection || selection.isCollapsed) return null;
  const text = selection.toString().trim();
  // Reject if contains whitespace (multi-word selection)
  if (/\s/.test(text)) return null;
  // Reject if it looks like punctuation only
  if (!/[a-zA-Z]/.test(text)) return null;
  return text;
}

// Walks text nodes inside `root` until it finds `targetNode`, accumulating
// character lengths. Returns the char offset of the selection start in the
// block's textContent, or -1 if targetNode isn't found in the text-node
// walk (e.g. the selection container is the block element itself).
function charOffsetIn(root, targetNode, offsetInTarget) {
  if (targetNode.nodeType === Node.TEXT_NODE) {
    let acc = 0;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    let node;
    while ((node = walker.nextNode())) {
      if (node === targetNode) return acc + offsetInTarget;
      acc += node.nodeValue.length;
    }
    return -1;
  }
  // Element container — `offsetInTarget` is a child index, not a char.
  // Sum lengths of preceding text descendants under that child.
  let acc = 0;
  for (let i = 0; i < offsetInTarget && i < targetNode.childNodes.length; i++) {
    acc += (targetNode.childNodes[i].textContent || '').length;
  }
  // Add lengths of text nodes before targetNode under root.
  // Cheap upper-bound estimate: linear walk.
  let foundBefore = 0;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (targetNode.contains(node)) break;
    foundBefore += node.nodeValue.length;
  }
  return foundBefore + acc;
}

// Smart-quote- and abbreviation-aware sentence segmentation, returning
// `[{ text, start, end }]` with char offsets into the original input.
// Mirrors `TextSegmentation#segment_sentences_with_offsets` (Ruby) so
// the front-end capture and back-end recapture pick the same boundaries.
function segmentSentencesWithOffsets(text) {
  if (!text) return [];

  const ABBR = /\b(Mr|Mrs|Ms|Dr|Prof|St|Ave|Blvd|Jr|Sr|vs|etc|approx|est|dept|govt|corp|inc|ltd|fig|no|vol|ch)\./gi;
  // Sentinel byte that won't appear in source text. Stand in for an
  // abbreviation period so it doesn't trigger a sentence split.
  const SENTINEL = '\x01';
  const protectedText = text.replace(ABBR, (m) => m.replace('.', SENTINEL));

  // Sentence boundary: end punctuation [.!?] optionally followed by a
  // closing quote (ASCII or curly), then whitespace, before an opening
  // capital or quote. Quote-awareness is the §3.13 partial fix — needed
  // for dialogue like `?" she said.\n"Maybe…"`.
  const re = /[.!?][”’"']?\s+(?=[A-Z“‘"'])/g;
  const out = [];
  let cursor = 0;
  let match;
  while ((match = re.exec(protectedText)) !== null) {
    const endIdx = match.index + match[0].length;
    const raw = protectedText.slice(cursor, endIdx).replace(new RegExp(SENTINEL, 'g'), '.');
    if (raw.trim()) {
      out.push({ text: raw.trim(), start: cursor, end: endIdx });
    }
    cursor = endIdx;
  }
  // Trailing remainder.
  if (cursor < protectedText.length) {
    const raw = protectedText.slice(cursor).replace(new RegExp(SENTINEL, 'g'), '.');
    if (raw.trim()) {
      out.push({ text: raw.trim(), start: cursor, end: protectedText.length });
    }
  }
  return out;
}
